import { useId, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

// The compact box the inline fields edit in: no border until hovered, a light fill (USA-1536).
const INLINE_BOX =
  'border-transparent bg-muted/50 px-2 text-sm shadow-none hover:border-input focus-visible:bg-background aria-invalid:border-destructive/60 aria-invalid:bg-destructive/5'

/**
 * The box InlineFieldGroup edits in, for a value with nothing saved to click yet: a new record's
 * fields, the amount on a quick action (USA-1580). A saved value is edited with
 * POInlineEditableField or InlineFieldGroup instead, and searches and messages use SearchInput and
 * the compose fields.
 */
export function InlineFieldInput({ className, ...props }: ComponentProps<typeof Input>) {
  return <Input className={cn('h-8', INLINE_BOX, className)} {...props} />
}

/** The multi-line InlineFieldInput, for a new record's longer text (a description). */
export function InlineFieldTextarea({ className, ...props }: ComponentProps<typeof Textarea>) {
  return <Textarea className={cn('min-h-16 py-1.5', INLINE_BOX, className)} {...props} />
}

export type InlineGroupField<K extends string> = {
  key: K
  /** The field's name; it is also the empty field's placeholder. */
  label: string
  required?: boolean
  autoComplete?: string
  /** Grid placement inside the six-column editor, e.g. `col-span-6` (default) or `col-span-2`. */
  className?: string
  /** A one-line box (default), a multi-line box, a pick from `options`, or a date (YYYY-MM-DD). */
  kind?: 'text' | 'textarea' | 'select' | 'date'
  options?: ReadonlyArray<{ value: string; label: string }>
  /** Keyboard hint for text boxes, e.g. `decimal` for amounts. */
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'tel'
  /** A unit at the end of a text box, e.g. `%` or `days`. */
  suffix?: string
  /** Leave the field out for this draft, e.g. discount days unless the terms carry a discount. */
  hidden?: (draft: Record<K, string>) => boolean
}

/**
 * Several related fields edited together in place (USA-1536), like an address. The read view is
 * one click target; clicking it turns every field into a compact text, multi-line or pick field
 * where it stood, and
 * Save (with an optional required reason) appears only once something changed. Escape or Cancel
 * puts everything back.
 */
export function InlineFieldGroup<K extends string>({
  label,
  fields,
  values,
  children,
  interactiveChildren = false,
  reason,
  invalid,
  notice,
  saveLabel = 'Save',
  labelled = false,
  pending = false,
  disabled = false,
  editing,
  onEditingChange,
  onSave,
}: {
  /** What the group edits, for screen readers ("Ship-to address"). */
  label: string
  fields: ReadonlyArray<InlineGroupField<K>>
  values: Record<K, string>
  /** The read view; clicking it starts editing. */
  children: ReactNode
  /**
   * The read view holds its own links, so it cannot sit inside a button: it renders as is and the
   * caller opens editing from its own Edit control.
   */
  interactiveChildren?: boolean
  /** Ask for a reason before saving; the reason is passed to `onSave`. */
  reason?: { label: string; placeholder?: string }
  /** Filled fields that still cannot be saved (a percent over 100); highlighted when Save is pressed. */
  invalid?: (draft: Record<K, string>) => ReadonlyArray<K>
  /**
   * One line shown above Save while there are changes, for a consequence worth knowing; a function
   * sees the draft, for a preview or a message about what was typed (USA-1580).
   */
  notice?: ReactNode | ((draft: Record<K, string>) => ReactNode)
  saveLabel?: string
  /** Name each field above its box, for amounts whose meaning a filled box no longer shows. */
  labelled?: boolean
  pending?: boolean
  disabled?: boolean
  editing: boolean
  onEditingChange: (editing: boolean) => void
  onSave: (next: Record<K, string>, reason: string) => void
}) {
  const id = useId()
  const formRef = useRef<HTMLFormElement>(null)
  const [draft, setDraft] = useState(values)
  const [why, setWhy] = useState('')
  const [missing, setMissing] = useState<ReadonlySet<K>>(new Set())
  const [wasEditing, setWasEditing] = useState(editing)
  // Every edit starts from the saved values, however it was opened.
  if (editing !== wasEditing) {
    setWasEditing(editing)
    if (editing) {
      setDraft(values)
      setWhy('')
      setMissing(new Set())
    }
  }
  const shownFields = (current: Record<K, string>) =>
    fields.filter((field) => !field.hidden?.(current))
  const shown = shownFields(draft)
  const dirty = shown.some((field) => draft[field.key].trim() !== values[field.key].trim())
  const shownNotice = typeof notice === 'function' ? notice(draft) : notice
  const problems = (current: Record<K, string>): ReadonlySet<K> => {
    const visible = shownFields(current)
    const keys = new Set(visible.map((field) => field.key))
    return new Set([
      ...visible
        .filter((field) => field.required && !current[field.key].trim())
        .map((field) => field.key),
      ...(invalid?.(current) ?? []).filter((key) => keys.has(key)),
    ])
  }

  const start = () => {
    if (disabled || pending) return
    onEditingChange(true)
  }

  const cancel = () => {
    if (pending) return
    onEditingChange(false)
  }

  const save = () => {
    if (!dirty || pending) return
    const empty = problems(draft)
    setMissing(empty)
    if (empty.size > 0) {
      requestAnimationFrame(() =>
        formRef.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus(),
      )
      return
    }
    if (reason && !why.trim()) {
      formRef.current?.querySelector<HTMLInputElement>(`#${CSS.escape(`${id}-reason`)}`)?.focus()
      return
    }
    onSave(draft, why.trim())
  }

  if (!editing) {
    if (interactiveChildren) return <>{children}</>
    return (
      <button
        type="button"
        onClick={start}
        disabled={disabled}
        aria-label={`Edit ${label}`}
        title="Click to edit"
        className="-mx-2 -my-1 block w-[calc(100%+1rem)] rounded-md px-2 py-1 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        {children}
      </button>
    )
  }

  return (
    <form
      ref={formRef}
      aria-label={label}
      noValidate
      className="-mx-2 space-y-2"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          cancel()
        }
      }}
    >
      <div className="grid grid-cols-6 gap-1">
        {shown.map((field, index) => {
          const flagged = missing.has(field.key)
          const setValue = (value: string) => {
            const next = { ...draft, [field.key]: value }
            setDraft(next)
            // A highlighted field stays highlighted until its value can be saved.
            if (missing.size > 0) {
              const still = problems(next)
              setMissing(new Set([...missing].filter((key) => still.has(key))))
            }
          }
          const span = labelled ? 'w-full' : (field.className ?? 'col-span-6')
          const boxClass = cn(INLINE_BOX, flagged && 'border-destructive/60 bg-destructive/5')
          const placeholder = labelled
            ? field.required
              ? ''
              : 'Optional'
            : `${field.label}${field.required ? '' : ' (optional)'}`
          const named = (control: ReactNode) =>
            labelled ? (
              <div key={field.key} className={cn('space-y-0.5', field.className ?? 'col-span-6')}>
                <span aria-hidden="true" className="block text-[11px] text-muted-foreground">
                  {field.label}
                </span>
                {control}
              </div>
            ) : (
              control
            )
          if (field.kind === 'select') {
            return named(
              <Select key={field.key} value={draft[field.key] || undefined} onValueChange={setValue} disabled={pending}>
                <SelectTrigger
                  autoFocus={index === 0}
                  aria-label={field.label}
                  aria-invalid={flagged || undefined}
                  className={cn('h-7 w-full', boxClass, span)}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {(field.options ?? []).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
          if (field.kind === 'textarea') {
            return named(
              <InlineFieldTextarea
                key={field.key}
                autoFocus={index === 0}
                aria-label={field.label}
                aria-invalid={flagged || undefined}
                placeholder={placeholder}
                value={draft[field.key]}
                disabled={pending}
                onChange={(event) => setValue(event.target.value)}
                className={cn(flagged && 'border-destructive/60 bg-destructive/5', span)}
              />
            )
          }
          const input = (
            <InlineFieldInput
              key={field.key}
              autoFocus={index === 0}
              aria-label={field.label}
              aria-invalid={flagged || undefined}
              placeholder={placeholder}
              autoComplete={field.autoComplete}
              inputMode={field.inputMode}
              type={field.kind === 'date' ? 'date' : undefined}
              value={draft[field.key]}
              disabled={pending}
              onChange={(event) => setValue(event.target.value)}
              className={cn('h-7', flagged && 'border-destructive/60 bg-destructive/5', field.suffix ? 'pr-12' : span)}
            />
          )
          if (!field.suffix) return named(input)
          return named(
            <div key={field.key} className={cn('relative', span)}>
              {input}
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {field.suffix}
              </span>
            </div>
          )
        })}
      </div>
      {dirty ? (
        <div className="space-y-2 border-t pt-2">
          {shownNotice && <div className="text-xs text-muted-foreground">{shownNotice}</div>}
          {reason && (
            <Input
              id={`${id}-reason`}
              aria-label={reason.label}
              placeholder={reason.placeholder ?? reason.label}
              value={why}
              disabled={pending}
              maxLength={2000}
              onChange={(event) => setWhy(event.target.value)}
              className="h-8 text-sm"
            />
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={cancel} disabled={pending}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              size="sm"
              loading={pending}
              loadingText="Saving..."
              disabled={Boolean(reason) && !why.trim()}
            >
              {saveLabel}
            </LoadingButton>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={cancel}>
            Done
          </Button>
        </div>
      )}
    </form>
  )
}
